import React, { useMemo } from 'react';
import { InputNumber, Typography } from '@duxton-web/ui';
import _ from 'lodash';
import { ValueType } from 'rc-input-number';
import styled from 'styled-components';

import { VariableType } from '@/constants';
import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import {
  getSchemaTypeByVariableType,
  JSONSchema,
  SchemaType,
} from '@/features/variable/settings/validation';
import {
  emptyRange,
  isValidRange,
} from '@/features/variable/settings/validation/string-range';
import { variableJsonSchemaUpdate } from '@/features/variable/store/actions/variable/action-creators';
import { isFieldChanged } from '@/features/variable/utils';
import { VariableInfo } from '@/lib/PlannerDTOs';

export const multipleOfToPrecisionConversion = (multipleOf: number) => {
  if (multipleOf == null) return;

  if (multipleOf === 0) return 0;

  const precisionArray = String(multipleOf).split('.');
  return precisionArray?.[1] ? precisionArray[1].length : 0;
};

const getVariableSchema = (variable: VariableInfo): JSONSchema => {
  if (!variable?.metadata?.jsonSchema)
    return {
      type: getSchemaTypeByVariableType(variable) as any,
      minimum: null,
      maximum: null,
      multipleOf: null,
    };

  return JSON.parse(variable?.metadata?.jsonSchema);
};

const Error = () => {
  const { variable } = useVariableContext();

  const schema = getVariableSchema(variable);
  const { minimum, maximum } = schema || {
    minimum: null,
    maximum: null,
  };

  if (!isValidRange(minimum, maximum))
    return (
      <Typography.Text type="danger">
        Min cannot be greater or equal than Max
      </Typography.Text>
    );

  if (emptyRange(minimum, maximum))
    return (
      <Typography.Text type="danger">
        Min and/or Max is/are required
      </Typography.Text>
    );

  return null;
};

const NumberRange = () => {
  const { variable, disabled, schemaDiff } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  const schema = getVariableSchema(variable);

  const debouncedHandleRangeChange = useMemo(
    () =>
      _.debounce(
        async (
          minimum: ValueType,
          maximum: ValueType,
          multipleOf: ValueType,
        ) => {
          if (
            typeof minimum === 'string' ||
            typeof maximum === 'string' ||
            typeof multipleOf === 'string'
          )
            return false;

          const updatedSchema = {
            type: getSchemaTypeByVariableType(variable) as SchemaType,
          };

          if (minimum != null) updatedSchema['minimum'] = minimum;

          if (maximum != null) updatedSchema['maximum'] = maximum;

          if (multipleOf != null) updatedSchema['multipleOf'] = multipleOf;

          dispatch(
            variableJsonSchemaUpdate(
              updatedSchema,
              !emptyRange(minimum, maximum) && isValidRange(minimum, maximum),
            ),
          );
        },
        500,
      ),
    [],
  );

  const rangeChange = (
    minimum: ValueType,
    maximum: ValueType,
    multipleOf: ValueType,
  ) => {
    debouncedHandleRangeChange(minimum, maximum, multipleOf);
  };

  const precisionToMultipleOfConversion = (precision: ValueType) => {
    if (!precision || typeof precision === 'string') return '';

    const precisionArray: number[] = [];

    for (let i = 0; i < precision - 1; i++) {
      precisionArray.push(0);
    }

    precisionArray.push(1);

    return `0.${precisionArray.join('')}`;
  };

  return (
    <div
      id="validation-range-container"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <SecondaryText>Valid Range</SecondaryText>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <InputNumber
          disabled={disabled}
          data-testid="number-range-min"
          className={`validation-prefix-input-number ${
            isFieldChanged(schemaDiff, 'minimum')
              ? 'changelog-item-variable-modified'
              : ''
          }`}
          prefix="MIN"
          value={schema?.minimum}
          onChange={(e) => rangeChange(e, schema?.maximum, schema?.multipleOf)}
          precision={multipleOfToPrecisionConversion(schema?.multipleOf)}
          style={{
            width: 210,
          }}
        />
        <InputNumber
          disabled={disabled}
          data-testid="number-range-max"
          className={`validation-prefix-input-number ${
            isFieldChanged(schemaDiff, 'maximum')
              ? 'changelog-item-variable-modified'
              : ''
          }`}
          prefix="MAX"
          value={schema?.maximum}
          onChange={(e) => rangeChange(schema?.minimum, e, schema?.multipleOf)}
          precision={multipleOfToPrecisionConversion(schema?.multipleOf)}
          status={isValidRange(schema?.minimum, schema?.maximum) ? '' : 'error'}
          style={{
            width: 210,
          }}
        />
      </div>

      {variable.type === VariableType.FLOAT64 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SecondaryText>Number of decimals (precision)</SecondaryText>
          <InputNumber
            disabled={disabled}
            data-testid="number-range-precision"
            className={`validation-input-number ${
              isFieldChanged(schemaDiff, 'multipleOf')
                ? 'changelog-item-variable-modified'
                : ''
            }`}
            min={0}
            precision={0}
            value={multipleOfToPrecisionConversion(schema?.multipleOf)}
            onChange={(e) =>
              rangeChange(
                schema?.minimum,
                schema?.maximum,
                Number(precisionToMultipleOfConversion(e)),
              )
            }
          />
        </div>
      )}
      <Error />
    </div>
  );
};

const SecondaryText = styled(Typography.Metadata)`
  color: #a3a3a3;
`;

export default NumberRange;
